import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import './App.css';
import cosylabLogo from './cosylab_logo.png'; 
import cookingGif from './assets/cooking.gif';
import remarkGfm from 'remark-gfm';

const SURPRISE_QUERIES = [
  "Give Substitute for dal",
  "Give 10 recipes using paneer",
  "Tell me everything about dal makhani",
  "substitute for panner without tofu",
  "Healthy snacks under 200 calories"
];

// ============================================================
// CONSTRAINT PARSER
// Detects phrases like "without butter", "no eggs", "other than
// dairy", "replacing sugar", "using only oats", etc.
// Returns [{ type: 'exclude'|'include'|'substitute', phrase, keyword }]
// ============================================================
const CONSTRAINT_PATTERNS = [
  // EXCLUSIONS
  { regex: /\bwithout\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,     keyword: 'without',    type: 'exclude'     },
  { regex: /\bno\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,           keyword: 'no',         type: 'exclude'     },
  { regex: /\bexcept\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,       keyword: 'except',     type: 'exclude'     },
  { regex: /\bexcluding\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,    keyword: 'excluding',  type: 'exclude'     },
  { regex: /\bavoiding\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,     keyword: 'avoiding',   type: 'exclude'     },
  { regex: /\bremoving\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,     keyword: 'removing',   type: 'exclude'     },
  { regex: /\bremove\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,       keyword: 'remove',     type: 'exclude'     },
  { regex: /\bother\s+than\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi, keyword: 'other than', type: 'exclude'     },
  { regex: /\bfree\s+of\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,   keyword: 'free of',    type: 'exclude'     },
  { regex: /\bskipping\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,     keyword: 'skipping',   type: 'exclude'     },
  { regex: /\bskip\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,         keyword: 'skip',       type: 'exclude'     },
  // SUBSTITUTIONS
  { regex: /\binstead\s+of\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,    keyword: 'instead of',  type: 'substitute' },
  { regex: /\breplace\s+(?:the\s+)?([\w\s,&]+?)(?=\s+with\b|\s+and\b|,|$)/gi,     keyword: 'replace',     type: 'substitute' },
  { regex: /\breplacing\s+(?:the\s+)?([\w\s,&]+?)(?=\s+with\b|\s+and\b|,|$)/gi,   keyword: 'replacing',   type: 'substitute' },
  { regex: /\bsubstitute\s+(?:for\s+)?([\w\s,&]+?)(?=\s+with\b|\s+and\b|,|$)/gi,  keyword: 'substitute',  type: 'substitute' },
  { regex: /\bsubstituting\s+(?:for\s+)?([\w\s,&]+?)(?=\s+with\b|\s+and\b|,|$)/gi,keyword: 'substituting',type: 'substitute' },
  { regex: /\bswapping\s+(?:out\s+)?(?:the\s+)?([\w\s,&]+?)(?=\s+with\b|\s+for\b|\s+and\b|,|$)/gi, keyword: 'swapping', type: 'substitute' },
  { regex: /\bswap\s+(?:out\s+)?(?:the\s+)?([\w\s,&]+?)(?=\s+with\b|\s+for\b|\s+and\b|,|$)/gi,     keyword: 'swap',     type: 'substitute' },
  // INCLUSIONS
  { regex: /\bwith\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,          keyword: 'with',      type: 'include' },
  { regex: /\busing\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,         keyword: 'using',     type: 'include' },
  { regex: /\bincluding\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,     keyword: 'including', type: 'include' },
  { regex: /\bonly\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,          keyword: 'only',      type: 'include' },
  { regex: /\bmust\s+have\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,   keyword: 'must have', type: 'include' },
  { regex: /\bmust\s+include\s+([\w\s,&]+?)(?=\s+and\b|\s+or\b|\s+but\b|,|$)/gi,keyword: 'must include', type: 'include' },
];

const NOISE_WORDS = new Set([
  'a','an','the','it','is','are','be','been','i','me','my','we','our',
  'you','your','this','that','these','those','just','also','very',
  'really','some','any','all','more','most','many','much','few',
  'recipe','recipes','dish','food','meal','version','way','type',
  'something','anything','everything','nothing','them','they',
]);

function cleanPhrase(raw) {
  return raw
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[.,!?;:]+$/, '')
    .split(' ')
    .filter(w => !NOISE_WORDS.has(w.toLowerCase()))
    .join(' ')
    .trim();
}

export function parseConstraints(text) {
  const constraints = [];
  const seen = new Set();
  for (const { regex, keyword, type } of CONSTRAINT_PATTERNS) {
    regex.lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      const phrase = cleanPhrase(match[1]);
      if (!phrase || phrase.length < 2) continue;
      const key = `${type}:${phrase.toLowerCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        constraints.push({ type, keyword, phrase });
      }
    }
  }
  return constraints;
}

// ============================================================
// COUNT DETECTOR
// Extracts an explicit requested count from the query.
// "10 substitutes", "top 30", "give me 5", "list 20" → number
// ============================================================
const NUMBER_WORDS = {
  one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,
  eleven:11,twelve:12,thirteen:13,fourteen:14,fifteen:15,sixteen:16,
  seventeen:17,eighteen:18,nineteen:19,twenty:20,thirty:30,forty:40,fifty:50,
};

export function parseCount(text) {
  // Numeric: "10 substitutes", "top 30", "give me 5 recipes", "list 20"
  const numMatch = text.match(
    /\b(?:top|give\s+me|list|show|find|suggest|provide|get|want)?\s*(\d+)\s*(?:substitutes?|alternatives?|options?|recipes?|ideas?|ways?|types?|kinds?|examples?|choices?)?\b/i
  );
  if (numMatch) return parseInt(numMatch[1], 10);

  // Word form: "five substitutes"
  for (const [word, num] of Object.entries(NUMBER_WORDS)) {
    const re = new RegExp(`\\b${word}\\b`, 'i');
    if (re.test(text)) return num;
  }
  return null;
}

// ============================================================
// INTENT DETECTOR
// Decides what the user actually wants:
//   'find_substitutes'  → alternatives TO an ingredient/dish
//   'make_recipe'       → a recipe for a dish
//   'general'           → other queries
// ============================================================
const SUBSTITUTE_FOR_PATTERNS = [
  /\bsubstitute[sd]?\s+for\b/i,
  /\balternative[s]?\s+(?:to|for)\b/i,
  /\breplace(?:ment)?[s]?\s+for\b/i,
  /\binstead\s+of\b/i,
  /\b(?:\d+|top)\s+(?:substitutes?|alternatives?|replacements?|options?)\s+(?:for|of|to)\b/i,
  /\bsubstitutes?\s+(?:of|for)\b/i,
  /\bswap[s]?\s+for\b/i,
  /\bwhat\s+can\s+(?:i|we)\s+use\s+instead\s+of\b/i,
  /\bwhat\s+(?:can\s+)?replace[s]?\b/i,
];

export function parseIntent(text) {
  for (const pattern of SUBSTITUTE_FOR_PATTERNS) {
    if (pattern.test(text)) return 'find_substitutes';
  }
  return 'general';
}

// Extract the TARGET of substitution ("substitutes for PANEER" → "paneer")
function extractSubstituteTarget(text) {
  const patterns = [
    /\bsubstitutes?\s+(?:for|of|to)\s+([\w\s]+?)(?:\s+without|\s+no\b|\s+except|\s+other\s+than|\s+excluding|$)/i,
    /\balternatives?\s+(?:to|for)\s+([\w\s]+?)(?:\s+without|\s+no\b|\s+except|\s+other\s+than|\s+excluding|$)/i,
    /\breplacements?\s+for\s+([\w\s]+?)(?:\s+without|\s+no\b|\s+except|\s+other\s+than|\s+excluding|$)/i,
    /\breplace\s+([\w\s]+?)\s+(?:with|in)/i,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m) return m[1].trim().replace(/[.,!?]+$/, '');
  }
  return null;
}

// ============================================================
// FEASIBILITY CHECK DETECTOR
// Detects yes/no questions: "can X be substituted with Y", etc.
// Returns { source, target } or null
// ============================================================
const FEASIBILITY_PATTERNS = [
  /can\s+([\w\s]+?)\s+be\s+substituted\s+with\s+([\w\s]+?)(?:\s*\?|$)/i,
  /can\s+(?:we|i|you)\s+substitute\s+([\w\s]+?)\s+with\s+([\w\s]+?)(?:\s*\?|$)/i,
  /can\s+(?:we|i|you)\s+change\s+([\w\s]+?)\s+with\s+([\w\s]+?)(?:\s*\?|$)/i,
  /can\s+([\w\s]+?)\s+replace\s+([\w\s]+?)(?:\s*\?|$)/i,
  /can\s+(?:i|we)\s+use\s+([\w\s]+?)\s+instead\s+of\s+([\w\s]+?)(?:\s*\?|$)/i,
  /is\s+([\w\s]+?)\s+(?:a\s+)?(?:good\s+)?substitute\s+for\s+([\w\s]+?)(?:\s*\?|$)/i,
  /will\s+([\w\s]+?)\s+work\s+(?:as|instead\s+of)\s+([\w\s]+?)(?:\s*\?|$)/i,
];
// group order: [sourceGroup, targetGroup]  (1-indexed capture groups)
const FEASIBILITY_ORDER = [
  [1,2],[1,2],[1,2],   // X with Y  -> source=X target=Y
  [2,1],[2,1],[2,1],[2,1], // Y replace/sub-for/work-as X -> source=X target=Y
];
export function parseFeasibilityCheck(text) {
  for (let i = 0; i < FEASIBILITY_PATTERNS.length; i++) {
    const m = text.match(FEASIBILITY_PATTERNS[i]);
    if (m) {
      const [sg, tg] = FEASIBILITY_ORDER[i];
      const source = m[sg]?.trim().replace(/[.,!?]+$/, '');
      const target = m[tg]?.trim().replace(/[.,!?]+$/, '');
      if (source && target) return { source, target };
    }
  }
  return null;
}

// ============================================================
// AUGMENTED MESSAGE BUILDER  (now intent + count aware)
// ============================================================
function buildAugmentedMessage(originalQuery, constraints) {
  // Feasibility questions need NO augmentation — model handles them directly
  if (parseFeasibilityCheck(originalQuery)) return originalQuery;

  const intent  = parseIntent(originalQuery);
  const count   = parseCount(originalQuery);
  const target  = extractSubstituteTarget(originalQuery);

  const excludes    = constraints.filter(c => c.type === 'exclude').map(c => c.phrase);
  const includes    = constraints.filter(c => c.type === 'include').map(c => c.phrase);
  const substitutes = constraints.filter(c => c.type === 'substitute').map(c => c.phrase);

  const hasConstraints = excludes.length || includes.length || substitutes.length;
  const needsAugment   = hasConstraints || intent === 'find_substitutes' || count !== null;

  if (!needsAugment) return originalQuery;

  const lines = [`User query: "${originalQuery}"\n`];

  // --- Intent clarification ---
  if (intent === 'find_substitutes' && target) {
    lines.push(`[INTENT]: The user wants a list of ALTERNATIVE INGREDIENTS or products that can REPLACE "${target}" in cooking.`);
    lines.push(`  ➜ Do NOT give a recipe FOR "${target}". Give actual substitutes/alternatives TO "${target}".`);
    if (excludes.length) {
      lines.push(`  ➜ Each substitute you suggest must itself NOT contain or require: ${excludes.join(', ')}.`);
      lines.push(`  ➜ Simply removing ${excludes.join('/')} from a "${target}" recipe does NOT count as a valid substitute.`);
    }
  }

  // --- Count enforcement ---
  if (count !== null) {
    lines.push(`\n[COUNT REQUIREMENT]: You MUST provide EXACTLY ${count} items. Not fewer, not more.`);
    lines.push(`  ➜ Number each item clearly (1. 2. 3. ... ${count}.).`);
    lines.push(`  ➜ Do not stop early. All ${count} must be genuinely distinct.`);
  }

  // --- Ingredient constraints ---
  if (hasConstraints) {
    lines.push('\n[STRICT INGREDIENT CONSTRAINTS — every item in your response must follow these]:');
    if (excludes.length)    lines.push(`  • EXCLUDE / DO NOT USE (in any suggestion): ${excludes.join(', ')}`);
    if (includes.length)    lines.push(`  • MUST INCLUDE / USE: ${includes.join(', ')}`);
    if (substitutes.length) lines.push(`  • Find a substitution for: ${substitutes.join(', ')}`);
    lines.push('\n  These constraints apply to EVERY item in your list, not just some of them.');
  }

  return lines.join('\n');
}

// ============================================================
// CHIP CONFIG — includes intent + count
// ============================================================
const CHIP_CONFIG = {
  exclude:    { label: '🚫 Exclude',      bg: '#fef2f2', border: '#fca5a5', color: '#b91c1c' },
  include:    { label: '✅ Include',      bg: '#f0fdf4', border: '#86efac', color: '#15803d' },
  substitute: { label: '🔄 Substitute',  bg: '#fffbeb', border: '#fcd34d', color: '#b45309' },
};

// Small badges for intent + count shown alongside chips
function QueryMetaBadges({ query }) {
  const intent = parseIntent(query);
  const count  = parseCount(query);
  if (intent === 'general' && count === null) return null;
  return (
    <div className="query-meta-badges">
      {intent === 'find_substitutes' && (
        <span className="meta-badge meta-badge-intent">🔍 Finding substitutes</span>
      )}
      {count !== null && (
        <span className="meta-badge meta-badge-count">🔢 Expecting {count} results</span>
      )}
    </div>
  );
}

function ConstraintChips({ constraints, title }) {
  if (!constraints.length) return null;
  return (
    <div className="constraint-chips-wrap">
      {title && <span className="constraint-chips-label">{title}</span>}
      <div className="constraint-chips-row">
        {constraints.map((c, i) => {
          const cfg = CHIP_CONFIG[c.type];
          return (
            <span
              key={i}
              className="constraint-chip"
              style={{ background: cfg.bg, borderColor: cfg.border, color: cfg.color }}
            >
              <span className="chip-type">{cfg.label}:</span>
              <span className="chip-phrase">{c.phrase}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// MARKDOWN COMPONENTS
// ============================================================
const sectionTracker = { current: "" };
 
function buildMarkdownComponents(checkedItems, setCheckedItems) {
  return {
 
    h1: ({ children }) => (
      <h1 className="recipe-main-title">{children}</h1>
    ),
 
    h2: ({ children }) => {
      const text = String(children).toLowerCase();
      sectionTracker.current = text;
      return <h2 className="section-header">{children}</h2>;
    },
 
    h3: ({ children }) => {
      const text = String(children).toLowerCase();
      sectionTracker.current = text;
      return <h3 className="section-subheader">{children}</h3>;
    },
 
    // Meta line under recipe title renders as a muted italic paragraph
    p: ({ children }) => {
      // Detect the italic meta line: *Region · Sub-region · Serves X · ...*
      const text = String(children || "");
      const isMeta = text.includes("·") && text.includes("min");
      return (
        <p className={isMeta ? "recipe-meta-line" : "recipe-paragraph"}>
          {children}
        </p>
      );
    },
 
    // ── Tables — always rendered as proper HTML tables ─────────────────
    table: ({ children }) => (
      <div className="table-scroll-wrapper">
        <table className="recipe-table">{children}</table>
      </div>
    ),
 
    thead: ({ children }) => <thead>{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr:    ({ children }) => <tr>{children}</tr>,
 
    th: ({ children }) => <th>{children}</th>,
 
    td: ({ children }) => {
      const text = String(children || "");
      // Calorie category badge colouring (green shades only)
      if (text === "Low")    return <td><span className="cal-badge cal-low">{children}</span></td>;
      if (text === "Medium") return <td><span className="cal-badge cal-medium">{children}</span></td>;
      if (text === "High")   return <td><span className="cal-badge cal-high">{children}</span></td>;
      if (text === "Unknown" || text === "N/A") return <td><span className="cal-badge cal-unknown">{children}</span></td>;
      return <td>{children}</td>;
    },
 
    // ── Lists — checkboxes ONLY in ingredient sections ─────────────────
    ul: ({ children }) => {
      const sec = sectionTracker.current;
      if (sec.includes("ingredient"))
        return <ul className="ingredients-list">{children}</ul>;
      return <ul className="generic-list">{children}</ul>;
    },
 
    ol: ({ children }) => (
      <ol className="instructions-list">{children}</ol>
    ),
 
    li: ({ children }) => {
      const sec  = sectionTracker.current;
      const key  = String(children);
      const isIngSection = sec.includes("ingredient");
 
      if (isIngSection) {
        const isChecked = checkedItems[key] || false;
        return (
          <li
            className={`ingredient-item ${isChecked ? "checked" : ""}`}
            onClick={() => setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }))}
          >
            <span className="ingredient-checkbox">{isChecked ? "✓" : ""}</span>
            <span className="ingredient-text">{children}</span>
          </li>
        );
      }
 
      // Everything else — plain li, no checkbox
      return <li className="generic-item">{children}</li>;
    },
 
    // ── Inline elements ────────────────────────────────────────────────
    strong: ({ children }) => <strong className="recipe-strong">{children}</strong>,
    em:     ({ children }) => <em className="data-note">{children}</em>,
    p:      ({ children }) => <p className="recipe-paragraph">{children}</p>,
 
    // ── Code blocks (for any technical output) ────────────────────────
    code: ({ children }) => <code className="inline-code">{children}</code>,
  };
}
// ============================================================
// FEASIBILITY CARD — rich yes/no answer renderer
// ============================================================
function parseFeasibilityAnswer(text) {
  const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);

  // Detect verdict from first line
  let verdictLine = lines[0] || '';
  let type = 'partial';
  if (/^✅|yes,?\s*you\s*can/i.test(verdictLine))      type = 'yes';
  else if (/^❌|not\s*recommended/i.test(verdictLine))  type = 'no';
  else                                                   type = 'partial';

  // Split remaining lines into paragraphs — last one is usually the tip
  const bodyLines = lines.slice(1);
  const paragraphs = [];
  let cur = '';
  for (const l of bodyLines) {
    if (!l) { if (cur.trim()) { paragraphs.push(cur.trim()); cur = ''; } }
    else cur += (cur ? ' ' : '') + l;
  }
  if (cur.trim()) paragraphs.push(cur.trim());

  // Heuristic: tip paragraph often starts with "To make", "Consider", "Note", etc.
  let tip = null;
  const tipIdx = paragraphs.findIndex(p =>
    /^(to make|consider|note|if you|however,\s+to|tip:|adjust|try)/i.test(p)
  );
  const body = tipIdx >= 0 ? paragraphs.filter((_, i) => i !== tipIdx) : paragraphs;
  if (tipIdx >= 0) tip = paragraphs[tipIdx];

  return { type, verdictLine, body, tip };
}

const VERDICT_CONFIG = {
  yes:     { bg: '#f0fdf4', border: '#22c55e', accent: '#15803d', icon: '✅', label: 'Yes, you can!',          badgeBg: '#dcfce7', badgeColor: '#166534' },
  partial: { bg: '#fffbeb', border: '#f59e0b', accent: '#b45309', icon: '⚠️', label: 'Partially — with care', badgeBg: '#fef3c7', badgeColor: '#92400e' },
  no:      { bg: '#fef2f2', border: '#ef4444', accent: '#b91c1c', icon: '❌', label: 'Not recommended',        badgeBg: '#fee2e2', badgeColor: '#991b1b' },
};

function FeasibilityCard({ answer, source, target }) {
  const { type, verdictLine, body, tip } = parseFeasibilityAnswer(answer);
  const cfg = VERDICT_CONFIG[type];

  // Extract the clean verdict text (strip leading emoji if present)
  const cleanVerdict = verdictLine.replace(/^[✅⚠️❌]\s*/u, '').trim();

  return (
    <div className="feasibility-card" style={{ borderColor: cfg.border }}>

      {/* ── Header: ingredient comparison ── */}
      <div className="feasibility-header" style={{ background: cfg.bg, borderBottomColor: cfg.border }}>
        <div className="feasibility-ingredient-box">
          <span className="feasibility-ingredient-label">Replacing</span>
          <span className="feasibility-ingredient-name">{source}</span>
        </div>
        <div className="feasibility-arrow">
          <span className="feasibility-arrow-icon">→</span>
          <span className="feasibility-arrow-sub">with</span>
        </div>
        <div className="feasibility-ingredient-box feasibility-ingredient-box--target">
          <span className="feasibility-ingredient-label">Proposed substitute</span>
          <span className="feasibility-ingredient-name">{target}</span>
        </div>
      </div>

      {/* ── Verdict banner ── */}
      <div className="feasibility-verdict-banner" style={{ background: cfg.badgeBg, borderColor: cfg.border, color: cfg.badgeColor }}>
        <span className="feasibility-verdict-icon">{cfg.icon}</span>
        <span className="feasibility-verdict-text">{cleanVerdict || cfg.label}</span>
      </div>

      {/* ── Explanation body ── */}
      <div className="feasibility-body">
        {body.map((para, i) => (
          <p key={i} className="feasibility-para">{para}</p>
        ))}
      </div>

      {/* ── Tip box ── */}
      {tip && (
        <div className="feasibility-tip-box" style={{ borderLeftColor: cfg.accent }}>
          <span className="feasibility-tip-icon">💡</span>
          <p className="feasibility-tip-text">{tip}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// APP
// ============================================================
function App() {
  const [query, setQuery]                       = useState('');
  const [answer, setAnswer]                     = useState('');
  const [isLoading, setIsLoading]               = useState(false);
  const [error, setError]                       = useState('');
  const [checkedItems, setCheckedItems]         = useState({});
  const [liveConstraints, setLiveConstraints]   = useState([]);
  const [appliedConstraints, setAppliedConstraints] = useState([]);

  const resultRef = useRef(null);

  // Parse constraints live as user types — suppress for yes/no feasibility questions
  useEffect(() => {
    if (parseFeasibilityCheck(query)) {
      setLiveConstraints([]);
    } else {
      setLiveConstraints(parseConstraints(query));
    }
  }, [query]);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;

    const isFeasibility    = !!parseFeasibilityCheck(query);
    const constraints      = isFeasibility ? [] : parseConstraints(query);
    const augmentedMessage = buildAugmentedMessage(query, constraints);

    setIsLoading(true);
    setError('');
    setAnswer('');
    setCheckedItems({});
    setAppliedConstraints(constraints);

    try {
      const response = await axios.post('http://127.0.0.1:5000/chat', {
        message: augmentedMessage,
        // Pass structured constraints separately — backend can use if needed
        constraints: constraints.length ? constraints : undefined,
      });
      setAnswer(response.data.reply);
    } catch (err) {
      console.error(err);
      setError("I couldn't reach the chef. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSurprise = (e) => {
    e.preventDefault();
    const randomQuery = SURPRISE_QUERIES[Math.floor(Math.random() * SURPRISE_QUERIES.length)];
    setQuery(randomQuery);
  };

  useEffect(() => {
    if (answer && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [answer]);

  const checkedCount     = Object.values(checkedItems).filter(Boolean).length;
  sectionTracker.current = ''; // reset before each render so sections don't bleed across queries
  const markdownComponents = buildMarkdownComponents(checkedItems, setCheckedItems);

  return (
    <div className="app-wrapper">

      <div className="hero-background">
        <header className="header">
          <div className="brand">
            <span className="brand-icon">🥗</span>
            <h1>RecipeDB Chatbot</h1>
          </div>
          <div className="logo-container">
            <img src={cosylabLogo} alt="User Logo" className="logo" />
          </div>
        </header>
        <div className="hero-content">
          <h2 className="hero-title">Your Intelligent Culinary Assistant</h2>
          <p className="hero-subtitle">Ask about recipes, substitutions, or dietary needs</p>
        </div>
      </div>

      <main className="main-content">

        <form onSubmit={handleSearch} className="search-container">
          <input
            type="text"
            className="search-input"
            // placeholder="e.g"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={isLoading}
          />

          {/* Live analysis: intent badge + constraint chips */}
          {!isLoading && query.trim().length > 3 && (() => {
            const feasibility = parseFeasibilityCheck(query);
            if (feasibility) {
              return (
                <div className="live-query-analysis">
                  <div className="constraint-chips-wrap">
                    <span className="constraint-chips-label">Detected question:</span>
                    <div className="constraint-chips-row">
                      <span className="constraint-chip feasibility-chip">
                        <span className="chip-type">🤔 Checking:</span>
                        <span className="chip-phrase">can <strong>{feasibility.target}</strong> replace <strong>{feasibility.source}</strong>?</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="live-query-analysis">
                <QueryMetaBadges query={query} />
                {liveConstraints.length > 0 && (
                  <ConstraintChips constraints={liveConstraints} title="Detected constraints:" />
                )}
              </div>
            );
          })()}

          <div className="button-group">
            <button type="submit" className="btn btn-primary" disabled={isLoading || !query.trim()}>
              {isLoading ? 'Cooking...' : 'Run Query'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleSurprise} disabled={isLoading}>
              Surprise Query!
            </button>
          </div>
        </form>

        {error && <div className="error-message">{error}</div>}

        {isLoading && (
          <div className="loading-container">
            <img src={cookingGif} alt="Cooking..." className="loading-gif" />
            <p className="loading-text">🍳 Cooking up your answer...</p>
          </div>
        )}

        {answer && (
          <div className="result-card fade-in" ref={resultRef}>

            {/* Applied constraints + intent banner */}
            {(() => {
              const feasibility = parseFeasibilityCheck(query);
              if (feasibility) {
                return (
                  <div className="applied-constraints-banner">
                    <span className="applied-banner-title">⚙️ Query interpreted as</span>
                    <div className="constraint-chips-row" style={{ marginTop: '0.5rem' }}>
                      <span className="constraint-chip feasibility-chip">
                        <span className="chip-type">🤔 Feasibility check:</span>
                        <span className="chip-phrase">can <strong>{feasibility.target}</strong> replace <strong>{feasibility.source}</strong>?</span>
                      </span>
                    </div>
                  </div>
                );
              }
              if (appliedConstraints.length > 0 || parseIntent(query) !== 'general' || parseCount(query) !== null) {
                return (
                  <div className="applied-constraints-banner">
                    <span className="applied-banner-title">⚙️ Query interpreted as</span>
                    <QueryMetaBadges query={query} />
                    {appliedConstraints.length > 0 && (
                      <ConstraintChips constraints={appliedConstraints} />
                    )}
                  </div>
                );
              }
              return null;
            })()}

            {/* Ingredient progress bar */}
            {checkedCount > 0 && (
              <div className="ingredient-progress-bar-wrap">
                <div
                  className="ingredient-progress-bar-fill"
                  style={{ width: `${(checkedCount / Math.max(Object.keys(checkedItems).length, 1)) * 100}%` }}
                />
                <span className="ingredient-progress-label">
                  {checkedCount} ingredient{checkedCount !== 1 ? 's' : ''} checked ✓
                </span>
              </div>
            )}

            <div className="markdown-content">
              {(() => {
                const feasibility = parseFeasibilityCheck(query);
                if (feasibility) {
                  return (
                    <FeasibilityCard
                      answer={answer}
                      source={feasibility.source}
                      target={feasibility.target}
                    />
                  );
                }
                return <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{answer}</ReactMarkdown>;
              })()}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
